import { ECOM_FEATURES, Feature } from '../models/feature';
import { SubscriptionPlanPrice } from '../models/subscriptionPlanPrice';
import { EcomEligibility } from '../services/subscription';

type PlanPriceKey = keyof SubscriptionPlanPrice;

interface EcomPlanPriceKeys {
  legacyMonthlyKey: PlanPriceKey;
  legacyAnnuallyKey: PlanPriceKey;
  ecomMonthlyKey: PlanPriceKey;
  ecomAnnuallyKey: PlanPriceKey;
}

/**
 * LaunchDarkly `subscriptionPlanPrice` keys per eCom feature: the legacy (listed) price key the
 * UI reads through `Feature.getFeaturePrefix`, and the `*Ecom*` key holding the self-serve price.
 * The ecom key doubles as the per-feature rollout switch (plan Decision 8): while it is not
 * seeded on the LD env, nothing is overridden and the listed price stays.
 */
export const ECOM_PLAN_PRICE_KEYS: Partial<Record<Feature, EcomPlanPriceKeys>> = {
  [Feature.PER_USER_KSE]: {
    legacyMonthlyKey: 'kseMonthly',
    legacyAnnuallyKey: 'kseAnnually',
    ecomMonthlyKey: 'kseEcomMonthly',
    ecomAnnuallyKey: 'kseEcomAnnually',
  },
  [Feature.UNLIMITED_ENGINE]: {
    legacyMonthlyKey: 'kreMonthly',
    legacyAnnuallyKey: 'kreAnnually',
    ecomMonthlyKey: 'kreEcomMonthly',
    ecomAnnuallyKey: 'kreEcomAnnually',
  },
  [Feature.TESTCLOUD_SESSION_CROSS_BROWSER]: {
    legacyMonthlyKey: 'testCloudCrossBrowserMonthly',
    legacyAnnuallyKey: 'testCloudCrossBrowserAnnually',
    ecomMonthlyKey: 'testCloudCrossBrowserEcomMonthly',
    ecomAnnuallyKey: 'testCloudCrossBrowserEcomAnnually',
  },
  [Feature.TESTCLOUD_SESSION_MOBILE_APP]: {
    legacyMonthlyKey: 'testCloudMobileMonthly',
    legacyAnnuallyKey: 'testCloudMobileAnnually',
    ecomMonthlyKey: 'testCloudMobileEcomMonthly',
    ecomAnnuallyKey: 'testCloudMobileEcomAnnually',
  },
  [Feature.TESTCLOUD_SESSION_LIVE_TESTING]: {
    legacyMonthlyKey: 'testCloudLiveTestingMonthly',
    legacyAnnuallyKey: 'testCloudLiveTestingAnnually',
    ecomMonthlyKey: 'testCloudLiveTestingEcomMonthly',
    ecomAnnuallyKey: 'testCloudLiveTestingEcomAnnually',
  },
  [Feature.TESTOPS_G3]: {
    legacyMonthlyKey: 'testOpsG3Monthly',
    legacyAnnuallyKey: 'testOpsG3Annually',
    ecomMonthlyKey: 'testOpsG3EcomMonthly',
    ecomAnnuallyKey: 'testOpsG3EcomAnnually',
  },
};

/**
 * Accounts eligible for a feature's eCom channel (no active legacy subscription of that feature;
 * KSE product#1447, extended to KRE / TestCloud / TestOps G3 by New Packaging Sep 2026) see the
 * ecom price everywhere that feature's price is displayed. The BE v2 preview/checkout transform
 * stays authoritative — this only keeps displayed prices in sync with what the preview will
 * charge. Only ecom keys that are defined override, and only for features whose eligibility is
 * `true`. Returns the same `flags` object when nothing changes.
 */
type WithPlanPrice = { subscriptionPlanPrice?: SubscriptionPlanPrice };

export const overridePlanPriceForEcom = <T extends WithPlanPrice>(
  flags: T | undefined,
  ecomEligibility: EcomEligibility,
): T | undefined => {
  if (!flags) {
    return flags;
  }
  const planPrice = flags.subscriptionPlanPrice ?? {};
  const overrides: SubscriptionPlanPrice = {};
  ECOM_FEATURES.forEach(feature => {
    const keys = ECOM_PLAN_PRICE_KEYS[feature];
    if (!keys || ecomEligibility[feature] !== true) {
      return;
    }
    const ecomMonthly = planPrice[keys.ecomMonthlyKey];
    if (ecomMonthly !== undefined) {
      overrides[keys.legacyMonthlyKey] = ecomMonthly;
    }
    const ecomAnnually = planPrice[keys.ecomAnnuallyKey];
    if (ecomAnnually !== undefined) {
      overrides[keys.legacyAnnuallyKey] = ecomAnnually;
    }
  });
  if (Object.keys(overrides).length === 0) {
    return flags;
  }
  return {
    ...flags,
    subscriptionPlanPrice: {
      ...planPrice,
      ...overrides,
    },
  };
};
